<script setup lang="ts">
import { fetchPerformancesData, queryKey, type InfoData } from '~/queries'

type Props = {
  product: Exclude<InfoData['product'], undefined>
  class?: string
}

const props = defineProps<Props>()

const classes = computed(() => ({
  'app-tickets-show': true,
  [`${props.class}`]: !!props.class,
}))

const modals = useModalsStore()
const site = useSiteStore()
const runtimeConfig = useRuntimeConfig()


useHead({
  script: [
    { tagPosition: 'bodyClose', src: runtimeConfig.public.ltd.polyfill, nomodule: true},
    { tagPosition: 'bodyClose', src: runtimeConfig.public.ltd.script, id: runtimeConfig.public.ltd.script_id },
  ]
})

const { data, pending } = await useAsyncData(
  queryKey('performances', props.product?.id),
  async () => {
    if (!props.product.hasPerformances) return

    const data = await fetchPerformancesData({ productId: props.product.id })

    return data
  },
  { server: false, lazy: true },
)

const performances = computed(() =>
  data.value?.items.map((performance) => ({
    ...performance,
    date: performance.dateTime.split(' ')[0],
  })),
)

const availableDates = computed(() => {
  const dates = performances.value?.map((item) => item.date).sort()

  return [...new Set(dates)].map((date) => new Date(date))
})

const minDate = computed(() => availableDates.value[0] ?? new Date())
const maxDate = computed(
  () => availableDates.value[availableDates.value.length - 1] ?? new Date(),
)

const disabledDates = computed(() => {
  const startDate = minDate.value
  const endDate = maxDate.value
  const datesArray = []

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    datesArray.push(new Date(d))
  }

  return datesArray.filter(
    (date) =>
      !availableDates.value.some(
        (availableDate) =>
          date.toISOString().split('T')[0] ===
          availableDate.toISOString().split('T')[0],
      ),
  )
})

const timeOptions = computed(
  () =>
    performances.value
      ?.filter((item) => item.date === date.value?.toISOString().split('T')[0])
      .map((item) => ({
        label: formatTime(item.dateTime),
        apiTime: item.dateTime.split(' ')[1],
        value: item.id,
        externalId: item.externalId,
      })) ?? [],
)

const quantity = ref(2)
const date = ref<Date | null>(null)
const time = ref<(typeof timeOptions.value)[number] | null>(null)

watch(date, () => (time.value = null))

const openModal = () => {
  if (!date || !time) return

  let payload = {
    product: props.product,
    quantity: quantity,
    date: date.value?.toISOString().split('T')[0],
    time: time.value.apiTime,
    performanceId: time.value.value,
    performanceExternalId: time.value.externalId,
  }

  if (props.product.source === 'ltd') {
    modals.openSeatWidgetLtdModal(payload)
  } else {
    modals.openSeatWidgetModal(payload)
  }
}
</script>

<template>
  <AppPaper :class="classes" class="relative p-3 pt-6 md:p-16">
    <template v-if="performances?.length || pending">
      <template v-if="product.source !== 'ltd'">
        <h3>{{ $t('component.tickets.show.quantity.label') }}</h3>
        <AppInputQuantity v-model="quantity" full-width size="lg" class="mb-6" />
      </template>

      <div class="row relative z-20 mb-6">
        <div class="col-md-6 mb-6">
          <h3>{{ $t('string.Date') }}</h3>

          <AppSelectDate
            v-model="date"
            :label="$t('string.Add-date')"
            full-width
            :disabled-dates="disabledDates"
            :min-date="minDate"
            :max-date="maxDate"
          />
        </div>

        <div class="col-md-6">
          <h3>{{ $t('string.Time') }}</h3>

          <AppSelect
            v-model="time"
            class="w-full"
            :title="$t('string.Add-time')"
            :options="timeOptions"
            full-width
            :class="{ 'pointer-events-none opacity-50': !date }"
          ></AppSelect>
        </div>
      </div>

      <AppButton
        full-width
        class="mt-8"
        :disabled="!date || !time"
        @click="openModal"
      >
        {{ $t('component.tickets.show.button') }}
      </AppButton>
    </template>

    <div v-else class="p-3 pt-6 md:p-6">
      <div v-if="product?.soldOut?.message" class="pb-4 reset-list-styles" v-html="product.soldOut.message"/>
      <p
        v-else
        v-html="$t('page.product.unavailable.description', {
      email: site.data?.emailAddress,
    })"
      />
      <AppButton v-if="product?.soldOut?.modal" full-width @click="modals.openContactUsModal()">{{ $t('page.faqs.extra.contact.title') }}</AppButton>
    </div>

    <Transition name="fade">
      <div
        v-if="pending"
        class="text-red absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-white/75"
      >
        <AppSpinner :style="{ fontSize: '2.5rem' }" />
      </div>
    </Transition>
  </AppPaper>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-tickets-show {
  h3 {
    margin: 0 0 0.75rem;
    font-family: $font-barlow;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.42857143;
    letter-spacing: 0.10714286em;
    text-transform: uppercase;
  }

  section {
    border: 1px solid $black;
    border-radius: 0.125rem;

    & > header {
      padding: 1rem;
      border-bottom: 1px solid $black;
    }
  }
}

.app-tickets-show__category {
  display: flex;
  align-items: center;

  header {
    p {
      margin: 0;
      font-family: $font-barlow;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 1.33333333;
      letter-spacing: 0.08333333em;
      text-transform: uppercase;
    }
  }

  h4 {
    margin: 0;
    font-size: inherit;
    line-height: 1.5;
    font-weight: 500;
    color: $black;
  }

  &__price {
    font-weight: 500;
    line-height: 1.5;
    color: $black;
    margin: 0 0.75rem 0 auto;
  }
}

.app-tickets-show__total {
  display: flex;
  justify-content: space-between;

  h4,
  p {
    margin: 0;
    font-size: 1.25rem;
    line-height: 1.5;
    font-weight: 500;
    color: $black;
  }
}
</style>
